import { In, IsNull } from 'typeorm'
import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive } from 'type-graphql'
import { Attachment } from '@things-factory/attachment-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import type ResolverContext from '@things-factory/auth-base'
import { Kpi } from './kpi'
import { KpiList } from './kpi-type'
import { KpiValue } from '../kpi-value/kpi-value'
import { KpiHistory } from './kpi-history'
import { Int } from 'type-graphql'
import { dedupByClosestDomain, getDomainIdsWithAncestors } from '../utils/domain-inheritance'

@Resolver(Kpi)
export class KpiQuery {
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [Kpi], { description: 'Fetch root KPIs (KPIs without parent)' })
  async kpisLevel0(@Ctx() context: ResolverContext): Promise<Kpi[]> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(Kpi).find({
      where: { domain: { id: In(domainIds) }, parent: IsNull() },
      order: { name: 'ASC' }
    })
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [Kpi], {
    description:
      'Fetch first level child KPIs. If rootId is provided, get children of that parent. If not, get all level 1 KPIs (children of root KPIs)'
  })
  async kpisLevel1(
    @Arg('rootId', {
      nullable: true,
      description: 'ID of the parent KPI to get level 1 children. If not provided, returns all level 1 KPIs'
    })
    rootId: string,
    @Ctx() context: ResolverContext
  ): Promise<Kpi[]> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    let whereCondition: any = { domain: { id: In(domainIds) } }

    if (rootId) {
      // 특정 부모의 자식들
      whereCondition.parent = { id: rootId }
    } else {
      // 모든 level 1 KPI들 (root KPI들의 직계 자식들)
      // 부모가 있으면서 부모의 부모가 null인 KPI들
      const kpis = await getRepository(Kpi)
        .createQueryBuilder('kpi')
        .leftJoinAndSelect('kpi.parent', 'parent')
        .leftJoinAndSelect('kpi.children', 'children')
        .leftJoinAndSelect('children.children', 'grandchildren')
        .where('kpi.domain IN (:...domainIds)', { domainIds })
        .andWhere('parent.id IS NOT NULL')
        .andWhere('parent.parent IS NULL')
        .orderBy('kpi.name', 'ASC')
        .getMany()

      return kpis
    }

    // rootId 제공된 경우
    const kpis = await getRepository(Kpi).find({
      where: whereCondition,
      relations: ['children', 'children.children'],
      order: { name: 'ASC' }
    })

    return kpis
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => Kpi!, { nullable: true, description: 'Fetch a single KPI by its unique identifier.' })
  async kpi(
    @Arg('id', { description: 'Unique identifier of the KPI to fetch.' }) id: string,
    @Ctx() context: ResolverContext
  ): Promise<Kpi> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(Kpi).findOne({
      where: { domain: { id: In(domainIds) }, id }
    })
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiList, { description: 'To fetch multiple Kpis' })
  async kpis(@Args(type => ListParam) params: ListParam, @Ctx() context: ResolverContext): Promise<KpiList> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    // closest-wins dedup 을 위해 pagination 은 dedup 이후 직접 적용한다.
    const { pagination, ...rest } = params || ({} as ListParam)

    const queryBuilder = getQueryBuilderFromListParams({
      // domain 필터링은 ancestor 기반으로 직접 적용 (shell helper 는 single-parent 만 지원)
      domain: undefined,
      params: rest as ListParam,
      repository: getRepository(Kpi),
      searchables: ['name', 'description'],
      filtersMap: {
        parent: { columnName: 'id', relationColumn: 'parent' }
      }
    })

    queryBuilder.andWhere(`${queryBuilder.alias}.domain IN (:...ancestorDomainIds)`, {
      ancestorDomainIds: domainIds
    })

    const allItems = await queryBuilder.getMany()
    const deduped = dedupByClosestDomain(allItems, domainIds, k => k.name)

    const { page = 1, limit } = pagination || {}
    const total = deduped.length
    const items =
      limit && limit > 0 ? deduped.slice(Math.max(0, (page - 1) * limit), Math.max(0, (page - 1) * limit) + limit) : deduped

    return { items, total }
  }

  @FieldResolver(type => [Kpi])
  async children(@Root() kpi: Kpi, @Ctx() context: ResolverContext): Promise<Kpi[]> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(Kpi).find({
      where: { domain: { id: In(domainIds) }, parent: { id: kpi.id } }
    })
  }

  @FieldResolver(type => String)
  async thumbnail(@Root() kpi: Kpi): Promise<string | undefined> {
    const attachment: Attachment = await getRepository(Attachment).findOne({
      where: {
        domain: { id: kpi.domainId },
        refType: Kpi.name,
        refBy: kpi.id
      }
    })

    return attachment?.fullpath
  }

  @FieldResolver(type => Domain)
  async domain(@Root() kpi: Kpi): Promise<Domain> {
    return kpi.domainId && (await getRepository(Domain).findOneBy({ id: kpi.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() kpi: Kpi): Promise<User> {
    return kpi.updaterId && (await getRepository(User).findOneBy({ id: kpi.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() kpi: Kpi): Promise<User> {
    return kpi.creatorId && (await getRepository(User).findOneBy({ id: kpi.creatorId }))
  }

  @FieldResolver(type => KpiValue, { nullable: true })
  async value(
    @Root() kpi: Kpi,
    @Ctx() context,
    @Arg('orgScope', { nullable: true, description: 'Organization scope filter for value' }) orgScope?: string
  ): Promise<KpiValue | null> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    let whereCondition: any = {
      domain: { id: In(domainIds) },
      kpi: { id: kpi.id }
    }

    // orgScope 필터가 있으면 KpiOrgScope로 필터링
    if (orgScope) {
      whereCondition.kpiOrgScope = { org: orgScope }
    }

    return await getRepository(KpiValue).findOne({
      where: whereCondition,
      relations: ['kpiOrgScope'],
      order: { valueDate: 'DESC' }
    })
  }

  @FieldResolver(type => Number, { nullable: true })
  targetValue(@Root() kpi: Kpi): number | undefined {
    return kpi.vizMeta?.targetValue
  }

  @FieldResolver(type => String, { nullable: true })
  unit(@Root() kpi: Kpi): string | undefined {
    return kpi.vizMeta?.unit
  }

  @FieldResolver(type => Kpi, { nullable: true })
  async parent(@Root() kpi: Kpi): Promise<Kpi | null> {
    if (!kpi.parentId) return null
    return await getRepository(Kpi).findOneBy({ id: kpi.parentId })
  }

  @FieldResolver(type => [KpiHistory], { nullable: true })
  async histories(
    @Root() kpi: Kpi,
    @Ctx() context,
    @Arg('limit', type => Int, { nullable: true }) limit?: number
  ): Promise<KpiHistory[]> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)
    const qb = getRepository(KpiHistory)
      .createQueryBuilder('history')
      .where('history.domain IN (:...domainIds)', { domainIds })
      .andWhere('history.originalId = :kpiId', { kpiId: kpi.id })
      .orderBy('history.updatedAt', 'DESC')
    if (limit) qb.limit(limit)
    else qb.limit(1)
    return await qb.getMany()
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [Kpi], { description: 'Fetch KPIs in hierarchical tree structure' })
  async kpiTree(@Ctx() context: ResolverContext): Promise<Kpi[]> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(Kpi).find({
      where: { domain: { id: In(domainIds) }, parent: IsNull() },
      relations: ['children', 'children.children', 'children.children.children'],
      order: { name: 'ASC' }
    })
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [Kpi], { description: 'Fetch child KPIs of a given parent KPI' })
  async kpiChildren(
    @Arg('parentId', { description: 'ID of the parent KPI' }) parentId: string,
    @Ctx() context: ResolverContext
  ): Promise<Kpi[]> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(Kpi).find({
      where: { domain: { id: In(domainIds) }, parent: { id: parentId } },
      order: { name: 'ASC' }
    })
  }
}
